import { AvatarSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { ICharacter, TypingMode, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import React from "react";
import { useParams } from "react-router-dom";

import { SiteLogo } from "@/components";
import AvatarWithModal from "@/components/common/AvatarWithModal/AvatarWithModal";
import { Col, LinkWrapper } from "@/components/customComponents";
import { CHAT_PAGE_WIDTH } from "@/constants";
import TypingAnimation from "@/pages/Chat/components/ChatBubble/TypingAnimation";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl, getProfilePathForCharacter } from "@/utils";

import { ANONYMOUS_DISPLAY_NAME } from "./constants";
import { getBubbleStyles } from "./utils";

type TypingBubbleProps = {
  character: ICharacter | null;
  typingMode: TypingMode | null;
  user_name?: string;
  user_avatar_url?: string;
};

// Typing (Empty) Bubble component
export const TypingBubble = ({ character, typingMode, user_name, user_avatar_url }: TypingBubbleProps): React.JSX.Element => {
  const { blurNSFW } = useUserSettings();
  const { isAuthenticated } = useUserInfo();
  const { characterId } = useParams();

  const isBotMessage = typingMode === TypingMode.BOT;
  const isUserMessage = typingMode === TypingMode.USER;
  const isAvatarImageBlurred = ((blurNSFW && character?.avatar_is_nsfw) || (!isAuthenticated && character?.is_nsfw)) && (isBotMessage || !typingMode);

  const messageAvatarUrl = isUserMessage ? user_avatar_url : character?.avatar_url;
  const messageName = isUserMessage ? user_name || ANONYMOUS_DISPLAY_NAME : character?.name;
  const imageSrc = getAvatarUrl(messageAvatarUrl ?? "", { preset: AvatarPreset.AVATAR_64X64 });

  const user = {
    src: imageSrc,
    name: messageName,
    blurred: isAvatarImageBlurred,
  };
  const nameFirstLetter = user?.name?.[0]?.toUpperCase();
  const handleProfileNavigation = () => {
    return getProfilePathForCharacter(character as ICharacter);
  };

  return (
    <>
      <div
        className={cn("w-full flex mb-lg bg-transparent items-center", isUserMessage ? "justify-end" : "justify-start")}
        style={{ maxWidth: CHAT_PAGE_WIDTH }}
      >
        <Col
          className={cn(
            "gap-md  w-full px-[13px] py-md",
            getBubbleStyles(isUserMessage),
            isUserMessage ? "!w-fit min-w-[300px] max-mob:min-w-[200px] !max-w-[650px]" : "!max-w-[650px]",
          )}
          style={{ maxWidth: CHAT_PAGE_WIDTH }}
        >
          <div className={cn("flex justify-between items-center gap-md", isUserMessage && "flex-row-reverse")}>
            <div className={cn("flex items-center gap-md ", isUserMessage && "flex-row-reverse")}>
              <AvatarWithModal
                size={AvatarSize.LARGE}
                id={characterId}
                src={messageAvatarUrl || ""}
                name={nameFirstLetter}
                blurred={user?.blurred}
                role={String(typingMode)}
                classNames={{
                  name: "!text-heading-4",
                  base: cn(!messageAvatarUrl && "bg-purple-9"),
                }}
                children={
                  !isAuthenticated &&
                  isUserMessage && (
                    <div className="bg-black p-sm rounded-md size-11">
                      <SiteLogo type="icon" className="size-full" theme="dark" />
                    </div>
                  )
                }
              />
              {user?.name && (
                <LinkWrapper to={isBotMessage ? handleProfileNavigation() : undefined}>
                  <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className={cn(isUserMessage ? "text-white" : "dark:text-gray-12 text-gray-12")}>
                    {user.name}
                  </Text>
                </LinkWrapper>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-md">
            <div className={cn("flex flex-col w-full [overflow-wrap:anywhere] py-[5px]")}>
              <TypingAnimation />
            </div>
          </div>
        </Col>
      </div>
    </>
  );
};

export default TypingBubble;
